import { FeatureFlagGate } from '@app/FeatureFlag/FeatureFlagGate'
import { FEATURE_FLAGS } from '@app/app-constants'
import { useAalForPortfolio, type AalFilters } from '@app/hooks/useAal'
import { AalStat } from './AalStat'

export interface PortfolioAalProps {
  element_layer_id: string | undefined
  hazard_id: string | undefined
  /**
   * Non-AEP scenario parameters held constant during integration. Typically
   * `hazardLayer.parameters` from the currently-displayed scenario.
   */
  filters?: AalFilters
  regions?: string[] | null
  draw_area?: number[] | null
  /** When true, render a denser caption line under the value. */
  showAssetCount?: boolean
}

/**
 * Renders the per-hazard AAL aggregated over an element layer (honouring
 * region masks and draw areas) under a specific scenario context. Self-gated
 * by the `aalReporting` flag.
 */
export const PortfolioAal = ({
  element_layer_id,
  hazard_id,
  filters,
  regions,
  draw_area,
  showAssetCount,
}: PortfolioAalProps) => {
  return (
    <FeatureFlagGate flag={FEATURE_FLAGS.AAL_REPORTING}>
      <PortfolioAalInner
        element_layer_id={element_layer_id}
        hazard_id={hazard_id}
        filters={filters}
        regions={regions}
        draw_area={draw_area}
        showAssetCount={showAssetCount}
      />
    </FeatureFlagGate>
  )
}

const PortfolioAalInner = ({
  element_layer_id,
  hazard_id,
  filters,
  regions,
  draw_area,
  showAssetCount,
}: PortfolioAalProps) => {
  const { data, isLoading, isError } = useAalForPortfolio({
    element_layer_id,
    hazard_id,
    filters,
    regions,
    draw_area,
  })

  if (isError) {
    return <AalStat aal_min={null} aal_max={null} reason="no_damage_ratio_data" />
  }

  const caption =
    showAssetCount && data && data.asset_count != null && data.asset_count > 0
      ? `across ${data.asset_count.toLocaleString()} element${data.asset_count === 1 ? '' : 's'}` +
        (data.missing_assets && data.missing_assets > 0
          ? `; ${data.missing_assets.toLocaleString()} missing data`
          : '')
      : undefined

  return (
    <>
      <AalStat
        aal_min={data?.aal_min}
        aal_max={data?.aal_max}
        reason={data?.reason}
        isLoading={isLoading}
      />
      {caption && (
        <span style={{ fontSize: '11px', color: '#6b7280', paddingLeft: '36px' }}>({caption})</span>
      )}
    </>
  )
}

export default PortfolioAal
